import { storeConfig } from '@/shared/storeConfig';
import {
  getMissionStatus,
  getMissionStatusRank,
  type MissionStatus,
} from '~/composables/useMissionButton';

type MissionUpdateCallback = ((data: TMissionOrBadge[]) => void) & {
  id?: string;
};

export const useSmarticoMissions = defineStore(
  'smarticoMissions',
  () => {
    const { debugLog } = useSmarticoConfigs();
    const authStore = useAuthStore();

    const missions = ref<TMissionOrBadge[]>([]);
    const isLoading = ref<boolean>(false);
    const isCallbackRegistered = ref<boolean>(false);
    const customUpdateCallbacks = ref<Array<MissionUpdateCallback>>([]);

    function handleMissionsUpdate(updatedMissions: TMissionOrBadge[]) {
      if (!updatedMissions || !Array.isArray(updatedMissions)) {
        debugLog('⚠️ onUpdate callback received invalid data for missions');

        return;
      }

      debugLog(
        `🔄 Missions updated via callback onUpdate: ${updatedMissions.length} missions`,
      );

      updatedMissions.forEach((updatedMission) => {
        const index = missions.value.findIndex(
          (mission) => mission.id === updatedMission.id,
        );

        if (index !== -1) {
          const newMissions = [...missions.value];

          newMissions[index] = updatedMission;
          missions.value = newMissions;
        } else {
          // Check if already exists to prevent duplicates
          const exists = missions.value.some(
            (mission) => mission.id === updatedMission.id,
          );

          if (!exists) {
            missions.value.push(updatedMission);
          }
        }
      });

      if (customUpdateCallbacks.value.length > 0) {
        customUpdateCallbacks.value.forEach((callback) => {
          try {
            callback(updatedMissions);
          } catch (err: any) {
            debugLog(
              `⭕️ ~ custom callback execution error ~ ${err.toString()}`,
            );
          }
        });
      }
    }

    function registerUpdateCallback() {
      if (isCallbackRegistered.value) {
        return;
      }

      try {
        if (window && window._smartico && window._smartico.api) {
          window._smartico.api.registerMissionsUpdateCallback?.(
            handleMissionsUpdate,
          );
          isCallbackRegistered.value = true;
          debugLog('✅ Missions update callback registered successfully');
        }
      } catch (err: any) {
        debugLog(`⭕️ ~ registerMissionsUpdateCallback ~ ${err.toString()}`);
      }
    }

    function waitForSmartico(timeout = 15000, interval = 500) {
      return new Promise((resolve, reject) => {
        const startTime = Date.now();
        const checkSmartico = () => {
          if (window && window._smartico) {
            resolve(true);
          } else if (Date.now() - startTime > timeout) {
            reject(new Error('Smartico API not available within timeout'));
          } else {
            setTimeout(checkSmartico, interval);
          }
        };

        checkSmartico();
      });
    }

    async function loadMissions() {
      try {
        isLoading.value = true;

        await waitForSmartico(); // Wait for Smartico to be ready

        registerUpdateCallback();

        let missionsPromise;

        if (authStore.isAuthenticated) {
          missionsPromise = window._smartico.api.getMissions({
            onUpdate: handleMissionsUpdate,
          });
        } else {
          missionsPromise = window._smartico.vapi('EN').getMissions({
            onUpdate: handleMissionsUpdate,
          });
        }

        const initialMissions = await missionsPromise;

        missions.value = [...initialMissions];

        debugLog(`📊 Loaded ${missions.value.length} missions initially`);
      } catch (err: any) {
        debugLog(`⭕️ ~ getMissionsError ~ ${err.toString()}`);
      } finally {
        isLoading.value = false;
      }
    }

    async function enterMission(missionId: number) {
      try {
        const result =
          await window._smartico.api.requestMissionOptIn(missionId);

        await loadMissions();

        return result;
      } catch (err: any) {
        debugLog(`⭕️ ~ requestMissionOptIn ~ ${err.toString()}`);
      }
    }

    async function requestMissionClaimReward(
      mission_id: number,
      ach_completed_id: number,
    ): Promise<TMissionClaimRewardResponse> {
      try {
        const result = await window._smartico.api.requestMissionClaimReward(
          mission_id,
          ach_completed_id,
        );

        debugLog(
          `✅ ~ requestMissionClaimReward success ~ ${JSON.stringify(result)}`,
        );

        await loadMissions(); // Force refresh after claim

        return {
          err_code: result.errCode,
          err_message: result.errMsg,
        };
      } catch (err: any) {
        debugLog(`⭕️ ~ requestMissionClaimReward ~ ${err.toString()}`);

        return {
          err_code: 999,
          err_message: err.toString(),
        };
      }
    }

    function registerCustomUpdateCallback(callback: MissionUpdateCallback) {
      if (typeof callback === 'function') {
        const exists = customUpdateCallbacks.value.some(
          (cb) => cb.id === callback.id,
        );

        if (!exists) {
          customUpdateCallbacks.value.push(callback);
          debugLog('✅ Custom missions update callback registered');
        }
      }
    }

    function unregisterCustomUpdateCallback(callback: MissionUpdateCallback) {
      const index = customUpdateCallbacks.value.findIndex(
        (cb) => cb.id === callback.id,
      );

      if (index !== -1) {
        customUpdateCallbacks.value.splice(index, 1);
        debugLog('✅ Custom missions update callback unregistered');
      }
    }

    function setMissionAsClaimed(missionId: number) {
      const index = missions.value.findIndex((m) => m.id === missionId);

      if (index !== -1) {
        missions.value[index] = {
          ...missions.value[index],
          prize_claimed_date_ts: new Date().getTime(),
        } as TMissionOrBadge;
      }
    }

    // Computed para missões filtradas (exclui only_in_custom_section)
    // Single computed that categorizes all missions at once for better performance
    const categorizedMissions = computed(() => {
      const categories: Record<MissionStatus, TMissionOrBadge[]> = {
        claimable: [],
        optIn: [],
        participating: [],
        locked: [],
        completed: [],
        expired: [],
      };

      for (const mission of missions.value) {
        if (mission.only_in_custom_section) continue;

        const status = getMissionStatus(mission);

        categories[status].push(mission);
      }

      // Ordena cada categoria por position (0, undefined, null vão pro final)
      for (const status of Object.keys(categories) as MissionStatus[]) {
        categories[status].sort((a, b) => {
          const posA = a.position || 0;
          const posB = b.position || 0;

          if (!posA && !posB) return 0;
          if (!posA) return 1;
          if (!posB) return -1;

          return posA - posB;
        });
      }

      return categories;
    });

    // Computed properties para arrays filtrados por status (derivados do categorizedMissions)
    const claimableMissions = computed(
      () => categorizedMissions.value.claimable,
    );

    const optInMissions = computed(() => categorizedMissions.value.optIn);

    const alreadyParticipatingMissions = computed(
      () => categorizedMissions.value.participating,
    );

    const lockedMissions = computed(() => categorizedMissions.value.locked);

    const completedMissions = computed(
      () => categorizedMissions.value.completed,
    );

    const expiredMissions = computed(() => categorizedMissions.value.expired);

    // Missões concluídas ordenadas (claimable primeiro)
    const claimedMissions = computed(() => {
      const claimable = claimableMissions.value;
      const completed = completedMissions.value;

      return [...claimable, ...completed];
    });

    return {
      missions,
      isLoading,
      // Missões filtradas por status
      claimableMissions,
      optInMissions,
      alreadyParticipatingMissions,
      lockedMissions,
      completedMissions,
      expiredMissions,
      claimedMissions,
      // Funções utilitárias exportadas
      getMissionStatus,
      getMissionStatusRank,
      // Ações
      loadMissions,
      enterMission,
      requestMissionClaimReward,
      registerCustomUpdateCallback,
      unregisterCustomUpdateCallback,
      setMissionAsClaimed,
      $reset: () => {
        missions.value = [];
        isLoading.value = false;
        isCallbackRegistered.value = false;
        customUpdateCallbacks.value = [];
      },
    };
  },

  storeConfig,
);
